import React from 'react';
import classNames from 'classnames';
import { useAppSelector } from 'src/store';
import { availableAreaColors } from 'src/shared/constants/colors';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import ConditionalField from 'src/shared/components/conditional-field/ConditionalField';
import moment from 'moment';
import { STANDARD_DATE_FORMAT } from 'src/shared/constants/dateConstants';
import { ProjectSamplingCadenceType } from 'src/shared/types';
import { hasInsightsFeature, hasHabitatInsightsFeature, isBasicProject, getFeaturesDisplayText } from 'src/shared/utils/featureHelpers';
import useAuthService from 'src/shared/hooks/useAuthService';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import usePermissions from 'src/shared/hooks/usePermissions';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import { Resources, Access } from 'src/shared/types';
import { UserGroup } from 'src/shared/types/graph/user';
import { User as PersonOutlineOutlinedIcon, ArrowLeft } from 'lucide-react';
import { SecondaryButton } from 'src/shared/components/button/Button';
import PolygonManagement from './components/polygon-management/PolygonManagement';
import styles from './ProjectDefinition.module.scss';
import EnablePdfDownload from './components/enable-pdf-download/EnablePdfDownload';
import ProjectDataRefresh from './components/project-data-refresh/ProjectDataRefresh';
import { getCurrentProjectId } from '../project-list/state/projectListSelector';
import { useProjectDefinitionQuery } from '../project-list/state/api/projectListGraphSlice';

const ProjectDefinition = () => {
    const navigate = useAppNavigation();
    const currentProjectId = useAppSelector(getCurrentProjectId);
    const { currentData: projectDefinition } = useProjectDefinitionQuery(
        {
            projectId: currentProjectId || '',
        },
        {
            skip: !currentProjectId,
        }
    );
    const { isCurrentUserSuperAdmin } = useAuthService();
    const { currentCustomerId } = useCustomer();
    const { hasPermission, currentUserBelongsToGroup } = usePermissions();
    const isBfxDataUploader = currentUserBelongsToGroup(UserGroup.BFX_DATA_UPLOADER);

    const canEditProject = hasPermission({
        to: [Access.CREATE, Access.UPDATE],
        customerId: currentCustomerId,
        resource: Resources.PROJECT_DEFINITION,
    });

    if (!projectDefinition) {
        return null;
    }

    const {
        customerName,
        projectName,
        projectCode,
        country,
        leadEmail,
        leadName,
        leadRole,
        samplingCadence,
        projectLifespanYears,
        sampleGroups,
        habitatAssay,
        projectEndDate,
        estimatedSampleCounts,
        estimatedSampleCountForTestTypes,
        createdAt,
        updatedAt,
        // TODO: ProjectDefinitionMap initialSampleEntries,
    } = projectDefinition;

    // filter and sort sampling events for first year
    const firstYearEvents =
        samplingCadence?.events.filter(event => event.year === 1).sort((a, b) => a.fromDate.localeCompare(b.fromDate)) || [];

    const mainContent = (
        <div className={styles.reviewAndSubmit} data-testid='project-definition'>
            {/* Project Header Metadata */}
            <div className={styles.projectHeader}>
                <div className={styles.projectHeaderContent}>
                    <div className={styles.projectNameSection}>
                        <div className={styles.projectNameLabel}>Project name</div>
                        <div className={styles.projectName} data-testid='project-name-header'>
                            {projectName}
                        </div>
                    </div>
                    <div className={styles.metadataSection}>
                        <div className={styles.metadataItem}>
                            <div className={styles.metadataLabel}>Last updated</div>
                            <div className={styles.metadataValue} data-testid='updated-at-header'>
                                {moment(updatedAt || createdAt).format('DD MMMM YYYY')}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.section}>
                <div className={classNames(styles.sectionTitle, 'flex items-center justify-between')} data-testid='project-details-section'>
                    <span>Project details</span>
                    {canEditProject && (
                        <SecondaryButton
                            onClick={() => navigate.toEditProjectDefinition({ projectId: currentProjectId || '' })}
                            className='text-sm'
                            data-testid='edit-project-button'
                        >
                            Edit
                        </SecondaryButton>
                    )}
                </div>
                <div className={styles.sectionItems}>
                    <div className={styles.sectionItem} data-testid='customer-name-item'>
                        <div className={styles.label}>Customer name</div>
                        <div className={styles.value} data-testid='customer-name'>
                            {customerName}
                        </div>
                    </div>

                    <div className={styles.sectionItem} data-testid='project-name-item'>
                        <div className={styles.label}>Project name</div>
                        <div className={styles.value} data-testid='project-name'>
                            {projectName}
                        </div>
                    </div>

                    <div className={styles.sectionItem} data-testid='project-code-item'>
                        <div className={styles.label}>Project code</div>
                        <div className={styles.value} data-testid='project-code'>
                            {projectCode}
                        </div>
                    </div>

                    <div className={styles.sectionItem} data-testid='features-item'>
                        <div className={styles.label}>Features</div>
                        <div className={styles.value} data-testid='features'>
                            {projectDefinition.features && projectDefinition.features.length > 0
                                ? getFeaturesDisplayText(projectDefinition.features)
                                : 'Starter (No features)'}
                        </div>
                    </div>

                    <div className={styles.sectionItem} data-testid='sampling-country-item'>
                        <div className={styles.label}>Sampling country</div>
                        <div className={styles.value} data-testid='sampling-country'>
                            {country}
                        </div>
                    </div>

                    <div className={styles.sectionItem} data-testid='project-end-date-item'>
                        <div className={styles.label}>
                            Project end date <QuestionButtonHelp type='api' slug='project-definition-end-date' />
                        </div>
                        <ConditionalField showWarning={false} isSatisfied={!isBasicProject(projectDefinition.features || [])}>
                            <div className={styles.value} data-testid='project-end-date'>
                                {moment(projectEndDate).format(STANDARD_DATE_FORMAT)}
                            </div>
                        </ConditionalField>
                    </div>
                </div>
                <div className={styles.sectionItems}>
                    <div className={styles.sectionItem} data-testid='customer-contact-item'>
                        <div className={styles.label}>Customer contact</div>
                        <div className={styles.customerContact}>
                            <div className={styles.userIcon}>
                                <PersonOutlineOutlinedIcon />
                            </div>
                            <div className={styles.contactDetails}>
                                <div className={styles.contactDetailsName} data-testid='lead-name'>
                                    {leadName}
                                </div>
                                <div data-testid='lead-email'>{leadEmail}</div>
                            </div>
                            <div className={styles.role}>
                                <div data-testid='lead-role'>{leadRole}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {hasInsightsFeature(projectDefinition.features || []) && (
                <div className={styles.section}>
                    <div className={styles.sectionTitle} data-testid='project-aims-section'>
                        <span>Project aims</span>
                    </div>
                    <div className={styles.sectionItems}>
                        <div className={styles.sectionItem} data-testid='sample-groups-item'>
                            <div className={styles.label}>
                                Sample groups <QuestionButtonHelp type='api' slug='project-definition-sample-groups' />
                            </div>
                            <div className={styles.sampleGroups} data-testid='sample-groups'>
                                {sampleGroups.map((entry, index) => {
                                    return (
                                        <div key={index} className={styles.sampleGroupTag} data-testid={`sample-group-${entry}`}>
                                            <div
                                                className={styles.sampleGroupTagColor}
                                                style={{ backgroundColor: availableAreaColors[index] }}
                                            ></div>
                                            <div className={styles.sampleGroupTagValue}>{entry}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className={styles.sectionItem} data-testid='habitat-assay-item'>
                            <div className={styles.label}>
                                Test <QuestionButtonHelp type='api' slug='project-definition-habitat-test-type' />
                            </div>
                            <div className={styles.habitatAssayTypes} data-testid='habitat-assay'>
                                {habitatAssay.map((entry, index) => {
                                    return (
                                        <div
                                            key={index}
                                            className={styles.habitatAssayTypeTag}
                                            data-testid={`habitat-assay-${entry.habitatAssayKey}`}
                                        >
                                            <div key={index} className={styles.habitatAssayTypeTagValue}>
                                                <div className={styles.habitatName}>{entry.habitatName}</div>
                                                <div className={styles.assayName}>{entry.assayName}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {hasInsightsFeature(projectDefinition.features || []) && (
                <div className={styles.section} data-testid='sampling-cadence-section'>
                    <div className={styles.sectionTitle}>
                        <span>Sampling cadence</span>
                    </div>
                    {samplingCadence ? (
                        <>
                            <div className={styles.sectionItems}>
                                {samplingCadence.type === ProjectSamplingCadenceType.MULTI_YEAR && (
                                    <div className={styles.sectionItem} data-testid='sampling-years-item'>
                                        <div className={styles.label}>
                                            Number of sampling years{' '}
                                            <QuestionButtonHelp type='api' slug='project-definition-sampling-years' />
                                        </div>
                                        <div className={styles.value} data-testid='sampling-years'>
                                            {projectLifespanYears}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {samplingCadence.type === ProjectSamplingCadenceType.MULTI_YEAR && (
                                <div className={styles.sectionItems} data-testid='first-year-events-note'>
                                    <div className={styles.samplingEventsNote}>
                                        <span className={styles.samplingEventsNoteTitle}>
                                            The following events are the first year events only.
                                        </span>
                                        <span className={styles.samplingEventsNoteText}>
                                            Events in the following year(s) will retain the same day and month selections
                                        </span>
                                    </div>
                                </div>
                            )}

                            <div className={styles.sectionItems} data-testid='first-year-events'>
                                <div className={styles.samplingEvents}>
                                    {firstYearEvents.map((entry, index) => {
                                        return (
                                            <div key={index} className={styles.samplingEvent} data-testid={`sampling-event-${index}`}>
                                                <div className={styles.eventNumber}>
                                                    Year 1, Event {index + 1}{' '}
                                                    <QuestionButtonHelp type='api' slug='project-definition-sampling-event' />
                                                </div>
                                                <div className={styles.eventName} data-testid={`event-name-${index}`}>
                                                    {entry.name}
                                                </div>
                                                <div className={styles.datesTitle}>Expected dates</div>
                                                <div className={styles.dates}>
                                                    <span className={styles.dateLabel}>From</span>
                                                    <span className={styles.dateValue} data-testid={`event-from-date-${index}`}>
                                                        {moment(entry.fromDate).format(STANDARD_DATE_FORMAT)}
                                                    </span>
                                                    <span className={styles.dateLabel}>To</span>
                                                    <span className={styles.dateValue} data-testid={`event-to-date-${index}`}>
                                                        {moment(entry.toDate).format(STANDARD_DATE_FORMAT)}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div data-testid='sampling-cadence-not-set'>Not set</div>
                    )}
                </div>
            )}
            {hasInsightsFeature(projectDefinition.features || []) && (
                <div className={styles.section} data-testid='samples-section'>
                    <div className={styles.sectionTitle}>
                        <span>Samples</span>
                    </div>
                    <div className={styles.sectionItems}>
                        <div className={styles.sectionItem} data-testid='total-samples-item'>
                            <div className={styles.label}>Total number of samples</div>
                            <div className={styles.value}>
                                <span className={styles.totalSamples} data-testid='total-samples'>
                                    {estimatedSampleCounts}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className={styles.sectionItems}>
                        <div className={styles.sectionItem} data-testid='samples-per-test-type-item'>
                            <div className={styles.label}>Number of samples per test type</div>
                            {estimatedSampleCountForTestTypes ? (
                                <div className={styles.samplesPerTestType} data-testid='samples-per-test-type'>
                                    {estimatedSampleCountForTestTypes.map((entry, index) => {
                                        return (
                                            <div
                                                key={index}
                                                className={styles.samplesPerTestTypeItem}
                                                data-testid={`test-type-item-${index}`}
                                            >
                                                <div className={styles.count} data-testid={`test-type-count-${index}`}>
                                                    {entry.count}
                                                </div>
                                                <div className={styles.habitatName} data-testid={`test-type-habitat-name-${index}`}>
                                                    {entry.type.habitatName}
                                                </div>
                                                <div className={styles.assayName} data-testid={`test-type-assay-name-${index}`}>
                                                    {entry.type.assayName}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div data-testid='samples-per-test-type-not-set'>Not set</div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {/* Actions Section - Clearly separated */}
            <div className={styles.actionsSection}>
                <div className={styles.sectionTitle} data-testid='actions-section'>
                    <span>Actions</span>
                </div>
                <div className={styles.actionsGrid}>
                    {isCurrentUserSuperAdmin && hasHabitatInsightsFeature(projectDefinition.features || []) && (
                        <div className={styles.actionCard} style={{ gridColumn: '1 / -1' }}>
                            <div className={styles.actionCardHeader}>
                                <div className={styles.actionCardTitle}>Polygon Management</div>
                            </div>
                            <div className={styles.actionCardContent}>
                                <PolygonManagement projectId={projectDefinition.projectId} />
                            </div>
                        </div>
                    )}
                    {isCurrentUserSuperAdmin && (
                        <div className={styles.actionCard}>
                            <div className={styles.actionCardHeader}>
                                <div className={styles.actionCardTitle}>Enable PDF Downloads</div>
                            </div>
                            <div className={styles.actionCardContent}>
                                <EnablePdfDownload pdfEventFiles={projectDefinition.files || []} projectId={projectDefinition.projectId} />
                            </div>
                        </div>
                    )}
                    {isBfxDataUploader && (projectDefinition.samplesCount ?? 0) > 0 && (
                        <div className={styles.actionCard}>
                            <div className={styles.actionCardHeader}>
                                <div className={styles.actionCardTitle}>Project Data Refresh</div>
                            </div>
                            <div className={styles.actionCardContent}>
                                <ProjectDataRefresh projectId={projectDefinition.projectId} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <StandardAppLayout
            title='Project definition'
            classNames={{ otherActions: 'p-0 gap-0 justify-normal' }}
            otherActions={
                <SecondaryButton onClick={() => navigate.toOrganisationManagement()} data-testid='back-button'>
                    <ArrowLeft size={16} />
                    Back
                </SecondaryButton>
            }
            mainContent={mainContent}
        />
    );
};

export default ProjectDefinition;
